/* Domain model for the Atelier studio ↔ client platform. */

export type ID = string;

export type StageKey =
  | "engagement" | "brief" | "concept" | "developed" | "presentation"
  | "documentation" | "construction" | "delivery";

export interface Stage {
  key: StageKey;
  label: string;
  blurb: string;
  status: "done" | "active" | "upcoming";
  startedOn?: string;
  completedOn?: string;
}

/** One named person on the client side — the primary is mirrored into the
 *  Client's own name/email/phone; the rest are additional representatives
 *  (project lead, finance contact, spouse, facilities manager…). */
export interface ClientContact {
  id: ID;
  name: string;
  role?: string;           // "Project lead", "Finance", "Facilities"…
  email?: string;
  phone?: string;
  primary?: boolean;       // the main point of contact
}

export interface Client {
  id: ID;
  name: string;            // contact person (the primary)
  company: string;
  email: string;
  phone?: string;
  avatarHue: number;       // for generated avatar
  since: string;
  code: string;            // portal access code
  /** Additional people on the client side beyond the primary contact. */
  contacts?: ClientContact[];
}

export type TeamRole = "admin" | "assistant" | "lead" | "designer" | "accountant" | "viewer";
export interface TeamMember {
  id: ID;
  name: string;
  email: string;
  role: TeamRole;
  code: string;            // studio access code
  status: "active" | "invited";
  avatarHue: number;
  /** What reaches this person's inbox. Absent = everything, immediately. */
  notify?: NotifyPrefs;
}

/** Per-person email preferences. `mode` throttles delivery; the flags choose
 *  which kinds of event are worth an email at all. */
export interface NotifyPrefs {
  mode: "instant" | "daily" | "off";
  clientActivity?: boolean;   // approvals, brief returns, design responses
  money?: boolean;            // invoices, payment claims, variations
  drawings?: boolean;         // reviews, RFIs, issued sets
  site?: boolean;             // site updates, snags
}

export type ApprovalStatus = "draft" | "sent" | "approved" | "changes";

export interface BriefSection {
  id: ID;
  title: string;
  body: string;
  hint?: string;           // guiding prompt shown to the client on the intake link
}
export interface BriefReference {
  id: ID;
  title: string;
  url?: string;
  note?: string;
  hue: number;
  addedBy?: string;        // "client" | studio member name
}
export type AreaUnit = "sqft" | "sqm";
export type SpaceZone = "Public" | "Private" | "Service" | "Outdoor" | "Circulation";
export interface SpaceReq {
  id: ID;
  name: string;       // "Primary Suite"
  qty: number;        // number of this space
  area: number;       // net area per unit
  zone?: SpaceZone;   // functional zone (for distribution)
  note?: string;      // sizing basis
}
export interface Brief {
  updatedOn: string;
  status: ApprovalStatus;
  sections: BriefSection[];
  references?: BriefReference[];
  spaces?: SpaceReq[];
  areaUnit?: AreaUnit;
  approvedBy?: string;
  approvedOn?: string;
  clientNote?: string;
}

/* ---- the brief, made checkable -------------------------------------------
 * Once the client approves the brief it stops being prose and becomes the
 * measure of the design: every space requirement and every substantive brief
 * section turns into a line the studio checks off as the design answers it —
 * and for spaces, records what the design actually PROVIDES, so target and
 * designed can be read against each other all the way through. */
export interface ChecklistItem {
  id: ID;
  label: string;
  kind: "space" | "requirement";
  spaceId?: ID;              // the brief space this line tracks
  sectionId?: ID;            // the brief section it came from
  done?: boolean;
  doneBy?: string;
  doneOn?: string;
  note?: string;             // how the design answers it
  /** Space lines only — what the design provides, in the brief's area unit. */
  designedQty?: number;
  designedArea?: number;     // net area per unit
}
export interface BriefChecklist {
  generatedOn: string;
  updatedOn: string;
  /** Shown to the client (progress + the target/designed report) when true. */
  sharedWithClient?: boolean;
  items: ChecklistItem[];
}

export interface ConceptBoard {
  id: ID;
  title: string;
  note: string;
  hue: number;             // stand-in artwork when no image is uploaded
  tags: string[];
  url?: string;            // an uploaded image of the direction
  createdOn?: string;
  /** The client's reaction once the directions are presented. */
  response?: {
    choice: "preferred" | "no";
    comment?: string;
    on: string;
    by: string;
  };
}

export type SpaceResponse = "pending" | "approved" | "revise";
export interface DesignSpace {
  id: ID;
  name: string;            // "Primary Suite"
  option: string;          // "Scheme B — Warm Minimal"
  description: string;
  hue: number;
  materials: string[];
  response: SpaceResponse;
  comment?: string;
  respondedOn?: string;
}
export interface Presentation {
  id: ID;
  title: string;
  issuedOn: string;
  status: ApprovalStatus;
  spaces: DesignSpace[];
}

export interface ScheduleEvent {
  id: ID;
  title: string;
  date: string;            // ISO yyyy-mm-dd
  kind: "milestone" | "meeting" | "site" | "deadline" | "internal";
  stage?: StageKey;
}

/** A job created from this project in the Project Studio (Bid or Project Manager). */
export interface StudioLinkRef { id: string; code: string; url: string; sheets: number; at: string; }

/** The work programme mirrored onto the Task Mind Map board. `nodes` maps our
 *  ProgramTask id → the mind-map node doing the work. */
export interface MindmapBoard {
  sheetId: string;
  url: string;
  nodes: Record<string, string>;
  /** Milestone key → the node carrying its date on the map. */
  milestones?: Record<string, string>;
  /** Drawing stages released to the board, keyed "SD"|"DD"|"CD". `nodes` maps
   *  each drawing's deliverable id → its mind-map node. */
  stages?: Record<string, { sentAt: string; nodes: Record<string, string> }>;
  pushedAt: string;
  lastProbedAt?: string;
  milestonesPushedAt?: string;
  datesPulledAt?: string;
}

/** Something worth telling the client about, surfaced in their portal. */
export interface ClientNotice {
  id: ID;
  on: string;
  title: string;
  body?: string;
  kind: "task" | "design" | "report" | "payment";
  seen?: boolean;
}

export type DeliverableStatus = "todo" | "doing" | "issued";
export type DeliverablePhase = "Conceptual" | "Design Development" | "IFT";
export type DeliverableKind = "drawing" | "schedule" | "document" | "visual";
/** One issue of a sheet — the drawing as it stood at a revision. The newest is
 *  current; older ones are superseded but kept for the record. `fileUrl` is the
 *  full-quality original in R2; `previewUrl` a rendered thumbnail; `page` the
 *  page inside a combined set PDF. */
/** A changed region between two revisions, in normalised page space (0..1).
 *  Rendered as a scalloped "revision cloud" over the sheet. */
export interface DrawingCloud { x: number; y: number; w: number; h: number }

export interface DrawingIssue {
  rev: string;             // "P1", "P2", "C1"
  issuedOn: string;
  by?: string;
  note?: string;           // what changed at this issue
  fileUrl?: string;        // full-quality original (R2)
  fileName?: string;
  previewUrl?: string;     // rendered preview image
  page?: number;           // page within the combined set, when applicable
  /** Regions that changed vs the previous revision — the smart-scan result,
   *  shown as revision clouds. Empty/undefined = not scanned or no change. */
  clouds?: DrawingCloud[];
}
export interface Deliverable {
  id: ID;
  code: string;          // "A-101"
  title: string;
  discipline: string;    // "Architecture" | "Structural" | "MEP" | "Interiors"
  phase: DeliverablePhase;
  status: DeliverableStatus;
  issuedOn?: string;
  /* Drawing-register detail. Optional — sheets created before this existed simply
     omit them, and the register renders without. */
  kind?: DeliverableKind;   // treated as "drawing" when absent
  /** Archived sheets are kept but hidden from the working register and the
   *  client — superseded or withdrawn, recoverable, not deleted. */
  archived?: boolean;
  /** Which building / drawing area this sheet belongs to, on a multi-building
   *  project. Sheet numbers repeat across buildings (A-101 in each), so the
   *  register nests by building and revisions match on building + number. */
  building?: string;
  scale?: string;           // "1:100", "1:5", "NTS"
  sheet?: string;           // sheet size — "A1", "A3"
  revision?: string;        // "P1", "C2" — the CURRENT revision (mirrors the last issue)
  contents?: string[];      // what the sheet actually covers
  dueOn?: string;           // scheduled due date once the stage is on the mind map
  /* Full-quality drawing lifecycle. `issues` is the revision chain (oldest →
     newest); the last entry is current and mirrors `revision`/`previewUrl`. */
  previewUrl?: string;      // current rendered preview
  fileUrl?: string;         // current full-quality original (R2)
  fileName?: string;
  page?: number;            // page within the source set PDF
  issues?: DrawingIssue[];
  /** Change regions on the CURRENT revision vs the one it superseded — mirrors
   *  the last issue's clouds, so the register and viewer can read them directly. */
  clouds?: DrawingCloud[];
  /** Original file signature, captured at upload — lets the duplicate scan match
   *  on content without re-fetching. `fileHash` is a SHA-256 of the file bytes. */
  fileSize?: number;
  fileHash?: string;
}

/** A frozen, numbered issue of the whole drawing set — the "overall revision".
 *  Every sheet's current revision at the moment it was issued, kept as a
 *  historical package the client and site can always pull. */
export interface DrawingSetIssue {
  id: ID;
  label: string;           // "IFT — Rev C", "Developed Design Issue 02"
  issuedOn: string;
  by?: string;
  note?: string;
  sharedWithClient?: boolean;
  sheets: { code: string; title: string; discipline: string; phase: DeliverablePhase; revision: string; building?: string; previewUrl?: string; fileUrl?: string; page?: number }[];
}

export type TaskStatus = "todo" | "doing" | "done" | "blocked";
export interface ProgramTask {
  id: ID;
  name: string;
  phase: string;
  owner: string;
  start: string;
  end: string;
  progress: number;        // 0..100
  status: TaskStatus;
}

export interface Report {
  id: ID;
  title: string;
  period: string;
  issuedOn: string;
  status: "issued" | "draft";
  summary: string;
  highlights: string[];
}

export interface QuoteLine {
  id: ID;
  label: string;
  qty: number;
  unit: string;
  rate: number;            // per unit
}
export type QuoteStatus = "draft" | "sent" | "accepted" | "declined";
export interface Quotation {
  id: ID;
  ref: string;
  title: string;
  issuedOn: string;
  status: QuoteStatus;
  lines: QuoteLine[];
  taxRate: number;         // 0..1
  note?: string;
}

/* ---- billing: the agreed payment structure, then invoices against it ---- */
export type MilestoneStatus = "scheduled" | "invoiced" | "paid";
/** One payable point in the agreed structure — a stage, a date or an event. */
/** One line of what a milestone actually covers. */
export interface MilestoneItem { id: ID; label: string; amount?: number; note?: string; }

export interface PaymentMilestone {
  id: ID;
  ref: string;              // PM-01
  label: string;            // "On signing" · "Concept design complete"
  basis?: string;           // what triggers it, in the contract's own words
  pct?: number;             // share of the contract sum, 0..1
  amount: number;
  dueOn?: string;
  status: MilestoneStatus;
  /** What `pct` is a share OF — design fees are often quoted as a % of the fee
   *  itself, while construction milestones quote a % of the construction cost. */
  pctBasis?: "fee" | "construction";
  /** Billed repeatedly (e.g. monthly across construction) rather than once. */
  recurring?: { every: "month" | "stage"; note?: string };
  /** Which payment stage this milestone belongs to (pre-construction etc.). */
  stage?: string;
  invoiceId?: ID;           // the invoice raised against it
  note?: string;
  /** What this payment covers, itemised — shown to the client on the link. */
  items?: MilestoneItem[];
}
/** The payment structure a project is billed under — read off the signed
    contract where there is one, otherwise built from the fee stages. */
/** A fee apportioned across the disciplines that earn it. */
export interface FeeDiscipline { name: string; pct?: number; amount: number; }
/** A high-level grouping of milestones — "pre-construction", "construction". */
export interface PaymentStageGroup { label: string; pct?: number; amount: number; }
/** Money held back until an event well after the work (not ordinary retention). */
export interface DeferredTerm { pct: number; trigger: string; note?: string; }

/** One scope line in a stage valuation — the way professional fees are actually
    certified: a value, how complete it is, and the deferral held off the earned
    amount. Sub-total, deferred and net are always derived, never stored. */
export interface ValuationLine {
  id: ID;
  scope: string;            // Architecture · Structural Engineering · MEP · Quantity Surveying
  value: number;            // full value of that scope within the stage
  pctComplete: number;      // 0..1
  note?: string;
}
/** A stage certified by percentage complete, e.g. Preconstruction. */
export interface StageValuation {
  id: ID;
  stage: string;            // "Preconstruction"
  deferredPct: number;      // 0..1 held off each line
  lines: ValuationLine[];
  certifiedOn?: string;
  invoiceId?: ID;           // the invoice raised for the net total
  /** Stages billed as monthly draws instead of one certification: each draw is
   *  the remaining balance divided by the months still left to run. */
  monthly?: { months: number; drawn: number; drawnAmount: number };
}

export interface PaymentSchedule {
  currency: string;
  /** The sum invoices are raised against — for design services this is the FEE. */
  contractSum: number;
  /** When the fee is a percentage of the construction cost, both are kept. */
  feeBasis?: "lump-sum" | "percentage-of-construction";
  constructionBudget?: number;
  feePct?: number;              // 0..1 of construction cost
  standardPct?: number;         // the undiscounted rate, where one is quoted
  /** The fee before any discount, where the studio states one. The client is
   *  shown it beside the agreed fee, with the percentage taken off. */
  fullFee?: number;
  disciplines?: FeeDiscipline[];
  stages?: PaymentStageGroup[];
  deferred?: DeferredTerm;
  /** Which mechanism actually bills this project. "valuation" supersedes the
   *  milestone list — the milestones stay on record as the engagement schedule
   *  but no longer drive invoicing or the totals, so nothing double-counts. */
  billingMode?: "milestones" | "valuation";
  /** Stages certified by percentage complete rather than by milestone event. */
  valuations?: StageValuation[];
  /** Chargeable extras outside the fee, with any included allowance. */
  additionalServices?: { label: string; rate: number; rateMax?: number; unit?: string; included?: number; note?: string }[];
  servicePeriodMonths?: number;
  exclusions?: string[];
  retentionPct?: number;
  paymentTermsDays?: number;     // net days from invoice
  /** A default hosted payment link for this project's invoices (a per-invoice
   *  `payUrl` overrides it). Card entry happens on the provider's page. */
  payUrl?: string;
  /** A second currency the studio reports costs in (overseas supply, consultants).
   *  `rate` is how many `costCurrency` units one `currency` unit buys, captured
   *  when it was last set — invoices snapshot it so history never re-values. */
  fx?: { costCurrency: string; rate: number; setOn: string };
  /** Recurring charges that invoice themselves (retainer, CA phase). */
  retainers?: Retainer[];
  /** Tax applied on top of every invoice raised from here — VAT/GST, toggleable.
   *  rate is 0..1. When enabled, an invoice's total is net × (1 + rate). */
  tax?: { enabled: boolean; label: string; rate: number };
  source: "contract" | "stages" | "manual";
  sourceNote?: string;           // the document it was read from, or "fee stages"
  milestones: PaymentMilestone[];
  /** An advance retainer taken up-front on appointment — a percentage of the
   *  design fee (default 15%), invoiced before work starts and credited back as
   *  the stages are billed. */
  retainer?: { pct: number; amount: number; note?: string; invoiceId?: string; creditPerStage?: boolean };
  createdOn: string;
  issuedOn?: string;             // when the client was shown it
}

export type InvoiceStatus = "due" | "paid" | "overdue" | "scheduled";
/** A payment received against an invoice — part payments are normal. */
export interface InvoicePayment {
  id: ID;
  amount: number;
  on: string;
  method?: string;          // transfer / cheque / card / cash
  reference?: string;
  note?: string;
  /** A client telling us they have paid is a CLAIM, not money in the account.
   *  Only the studio confirms, and only confirmed money counts anywhere.
   *  Absent means confirmed — everything recorded before this existed was
   *  entered by the studio in the first place. */
  status?: "claimed" | "confirmed";
  by?: string;              // who submitted it
  /** The client's evidence — a transfer slip, screenshot or receipt. */
  proof?: { url: string; name: string };
}

export interface Invoice {
  id: ID;
  ref: string;
  title: string;
  amount: number;          // the TOTAL owed — tax-inclusive when tax applied
  /** Set when tax was applied at issue: the pre-tax amount and the rate used,
   *  captured on the invoice so a later change to the setting never rewrites
   *  what was already billed. `amount` = net × (1 + taxRate). */
  net?: number;
  taxRate?: number;        // 0..1
  taxLabel?: string;
  issuedOn: string;
  dueOn: string;
  status: InvoiceStatus;
  paidOn?: string;
  /** A hosted checkout/payment link (Stripe, bank) the client can pay through —
   *  card data never touches this app. Overrides the schedule's default link. */
  payUrl?: string;
  /** The FX rate in force when this invoice was raised, so a later rate change
   *  never re-values what was already billed. */
  fxRate?: number; fxCurrency?: string;
  /** Receipts against it — the invoice is settled when these cover the amount. */
  payments?: InvoicePayment[];
  /** What it bills, line by line — an expenses invoice lists each receipt. */
  items?: { label: string; amount: number; note?: string; date?: string }[];
  /** Fee stage, reimbursable expenses, retainer, variation. */
  kind?: "fee" | "expenses" | "retainer" | "variation";
  /** The PDF as last sent to the client, stored on the project. */
  pdf?: { url: string; name: string };
  sentOn?: string;
  /** Automatic reminders — once before it is due, then weekly while overdue. */
  dueRemindedOn?: string;
  overdueRemindedOn?: string;
}

export interface FeedbackItem {
  id: ID;
  author: "client" | "studio";
  authorName: string;
  context: string;         // e.g. "Primary Suite — Scheme B"
  body: string;
  createdOn: string;
  resolved: boolean;
  replies: { id: ID; author: "client" | "studio"; authorName: string; body: string; createdOn: string }[];
}

/** A revision note the client (or studio) marks on a drawing — a highlighted
 *  region of a sheet with a comment, and a reply thread. Anchored by sheetKey
 *  (building/code) so it follows the sheet across revisions and works for the
 *  client, whose snapshot sheets carry no deliverable id. */
export interface DrawingNote {
  id: ID;
  sheetKey: string;        // `${building}/${code}` or just `${code}`
  code: string;
  rev?: string;            // the revision it was raised against
  region?: { x: number; y: number; w: number; h: number };  // normalized 0..1 highlight
  body: string;
  author: "client" | "studio";
  by: string;
  on: string;
  resolved?: boolean;
  replies?: { id: ID; author: "client" | "studio"; by: string; body: string; on: string }[];
}

export interface Activity {
  id: ID;
  on: string;              // ISO datetime
  who: string;
  what: string;
  kind: "brief" | "design" | "payment" | "schedule" | "report" | "feedback" | "stage";
}

/* ---- documents of record ----------------------------------------------
   Every formal event between the parties is emailed to BOTH of them when it
   happens, and the outcome of that send is kept here — so "did the client get
   the signed engagement?" has an answer, and a failed send can be re-sent. */
export type RecordKind = "engagement-issued" | "engagement-signed" | "approval-issued" | "approval-signed" | "approval-declined" | "brief-approved" | "certificate-issued" | "invoice-issued" | "invoice-reminder" | "statement-issued" | "payment-received" | "activity-logged" | "daily-log" | "weekly-update";
export const RECORD_LABEL: Record<RecordKind, string> = {
  "payment-received": "Payment receipt",
  "activity-logged": "Activity notice",
  "daily-log": "Daily activity log",
  "weekly-update": "Weekly project update",
  "statement-issued": "Account standing sent",
  "invoice-issued": "Invoice sent",
  "invoice-reminder": "Payment reminder sent",
  "engagement-issued": "Engagement issued",
  "engagement-signed": "Engagement signed",
  "approval-issued": "Approval requested",
  "approval-signed": "Drawings approved",
  "approval-declined": "Approval declined",
  "brief-approved": "Brief approved",
  "certificate-issued": "Payment certificate issued",
};

/** A payment certificate issued under the construction contract, mirrored here
 *  from the Contract Studio (contracts.studiogy.design). The certificate and the
 *  contractor's invoice live there; this record carries the figures and links so
 *  the client sees both in the portal and the studio sends them on as a document
 *  of record. */
export interface PaymentCertRecord {
  id: ID;
  number: string;            // IPC 3 · IPC.A.2 · 004
  series: string;            // advance | interim | final | supply | services | other
  title: string;             // "Interim payment (IPC)"
  contractName: string;
  payee: string;
  amount: number;
  currency: string;
  date: string;
  status: string;
  description?: string;
  shareUrl?: string;         // live certificate on the Contract Studio
  invoice?: { name: string; url: string; size?: number };
  emails?: { to: string[]; cc?: string[]; subject: string; at: string }[];
  receiptConfirmedBy?: string;
  updatedAt: string;
  source: "contract-gen";
}
export interface RecordRecipient {
  party: "client" | "studio";
  to: string;
  result: "pending" | "delivered" | "simulated" | "failed";
  error?: string;
  on?: string;
}
export interface SentRecord {
  id: ID;
  kind: RecordKind;
  refId?: string;          // the approval it concerns, when it concerns one
  title: string;
  by: string;
  on: string;
  recipients: RecordRecipient[];
  /** A file that went with it — the invoice PDF, stored on the project. */
  attachment?: { url: string; name: string };
}

/* ===========================================================================
   EXPENSES — money the studio lays out on the client's behalf: a permit fee,
   printing, a flight to site, a model. Logged in seconds with the receipt,
   approved by the studio, and billed back on their own invoice.
   ========================================================================= */
export type ExpenseCategory = "permits" | "travel" | "printing" | "models" | "consultants" | "courier" | "materials" | "other";
export const EXPENSE_CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  permits: "Permits & statutory", travel: "Travel & site visits", printing: "Printing & plans", models: "Models & samples",
  consultants: "Consultants & tests", courier: "Courier & freight", materials: "Materials bought", other: "Other",
};
export type ExpenseStatus = "logged" | "approved" | "billed" | "reimbursed" | "rejected";
export interface Expense {
  id: ID;
  ref: string;                 // EXP-012
  date: string;
  category: ExpenseCategory;
  description: string;
  amount: number;              // in the project currency
  vendor?: string;
  paidBy: string;
  /** Handling mark-up the client is billed on top, when agreed. */
  markupPercent?: number;
  reimbursable: boolean;       // false = a studio cost kept for the record
  receipt?: { url: string; name: string };
  note?: string;
  status: ExpenseStatus;
  loggedBy: string;
  loggedOn: string;
  approvedBy?: string;
  approvedOn?: string;
  rejectedNote?: string;
  invoiceId?: ID;
  reimbursedOn?: string;
}

export interface MoodImage {
  id: ID;
  src: string;           // compressed JPEG data URI
  caption?: string;
  addedBy: "client" | "team";
  addedByName: string;
  addedOn: string;
}
export interface Swatch {
  id: ID;
  name: string;          // "Smoked oak"
  hex: string;           // "#6b5842"
  material?: string;     // "Timber" | "Stone" | "Textile" …
  note?: string;
}
export interface MoodBoard {
  images: MoodImage[];
  swatches: Swatch[];
}

export type RemarkAuthor = "client" | "team";
/** The studio's answer to a client change request: what changed, on which
 *  revision, and a photo highlighting the changed area on the drawing. */
export interface RemarkResponse {
  body: string;          // the change made in response to the request
  revision?: string;     // the revision that carries the change, e.g. "P2"
  url?: string;          // markup photo / image of the revised implementation
  respondedOn: string;
  by: string;            // studio member who responded
  /** The client's approval of the revised implementation. */
  accepted?: { by: string; on: string };
}
export interface Remark {
  id: ID;
  author: RemarkAuthor;
  authorName: string;
  body: string;
  createdOn: string;
  resolved: boolean;
  onRevision: string;    // revision the remark was made against
  attachmentUrl?: string;      // an uploaded comment — a marked-up sheet or photo
  response?: RemarkResponse;   // the studio's coordinated response to the request
}
export interface ReviewSheet {
  id: ID;
  code: string;          // "A-101"
  title: string;
  discipline: string;
  revision: string;      // "P1", "P2" …
  hue: number;
  remarks: Remark[];
  url?: string;          // the uploaded drawing image (else the hue plate stands in)
  summary?: string;      // what the current (revised) drawing shows / what changed
  revisedOn?: string;    // when the latest revision was issued
}
export interface DrawingReview {
  id: ID;
  title: string;
  createdOn: string;
  status: "open" | "closed";
  sharedWithClient: boolean;
  access: string[];      // team member emails granted access (admin always has access)
  sheets: ReviewSheet[];
  summary?: string;      // AI summary of remarks & changes
  summaryOn?: string;
}

export type ConsultantDiscipline = "Structural" | "MEP";
export type ConsultantStatus = "appointed" | "proposed" | "declined";
/** A specialist sub-consultant the client appoints for a discipline the studio
 *  doesn't carry in-house (structural, MEP). The studio coordinates them, but
 *  the appointment — and the commercial relationship — is the client's. */
export interface Consultant {
  id: ID;
  discipline: ConsultantDiscipline;
  firm: string;
  contact?: string;        // named contact at the firm
  email?: string;
  phone?: string;
  note?: string;           // scope / remit note
  status: ConsultantStatus;
  appointedBy: "client" | "studio";
  appointedOn: string;
}

export interface Engagement {
  status: "draft" | "issued" | "signed";
  issuedOn?: string;
  signedOn?: string;
  signedBy?: string;
  signatureDataUri?: string;
  limitations: string[];   // limitations / exclusions of service
  terms: string;           // engagement terms
}

/** A bundle of deliverables shared out on a tokened public link — the client,
 *  a consultant or the contractor opens it with no sign-in, downloads the
 *  sheets, and (on a client package) leaves revision comments. */
export interface ProjectPackage {
  id: ID;
  ref: string;                    // PKG-001
  token: string;                  // public share link
  title: string;
  audience: "client" | "consultant" | "contractor";
  recipientName?: string;
  recipientEmail?: string;
  deliverableIds: ID[];
  note?: string;
  sharedBy: string;
  sharedAt: string;
  viewedAt?: string;
  clientNotes?: PackageNote[];
  /** Recipient's confirmation of receipt — closes the transmittal loop. */
  receipt?: { by: string; at: string };
}
export interface PackageNote {
  id: ID;
  code?: string;                  // which sheet the note is about
  text: string;
  by: string;
  at: string;
  resolved?: boolean;
  resolvedAt?: string;
}

export interface Project {
  id: ID;
  name: string;
  code: string;
  client: Client;
  type: string;            // display label ("Private Residence" or "Retail · Residential")
  types?: string[];        // building type(s) — >1 for mixed-use
  location: string;
  area: string;            // display area, e.g. "540 m²"
  gia?: number;            // overall gross internal area (numeric)
  giaUnit?: "sqft" | "sqm";
  floors?: number;         // typical (repeating) storeys — scales stacked programmes e.g. multi-family
  budget: number;
  currency: string;
  surveyPlan?: "yes" | "no" | "unknown";
  /** A survey / site plan the client supplied when none was on file. */
  surveyDoc?: { name: string; url?: string; imageId?: string; addedOn: string };
  geo?: { lat: number; lng: number };
  cover: number;           // hue
  createdOn: string;
  stages: Stage[];
  brief: Brief;
  /** The approved brief as a working checklist + target/designed space audit. */
  briefChecklist?: BriefChecklist;
  /** Formal stage submissions — the packaged close of each design stage. */
  submissions?: StageSubmission[];
  /** Design submissions and cost proposals received from tokened partners. */
  partnerSubmissions?: PartnerSubmission[];
  partnerProposals?: PartnerProposal[];
  concepts: ConceptBoard[];
  presentation: Presentation;
  schedule: ScheduleEvent[];
  program: ProgramTask[];
  deliverables?: Deliverable[];
  /** Drawing / document packages shared out on tokened links. */
  packages?: ProjectPackage[];
  /** Buildings / drawing areas on this project — for multi-building schemes
   *  (a church + hall + ancillary block) where each carries its own set. */
  buildings?: string[];
  /** Frozen, numbered issues of the whole drawing set — the overall-revision history. */
  drawingSets?: DrawingSetIssue[];
  /** Client/studio revision notes marked on drawings (highlighted regions + threads). */
  drawingNotes?: DrawingNote[];
  /** Jobs opened from this project in the Project Studio, keyed by module. */
  links?: { pm?: StudioLinkRef; bid?: StudioLinkRef; contract?: StudioLinkRef };
  /** The work programme's board on the Task Mind Map. */
  mindmap?: MindmapBoard;
  /** Client-facing notifications (newest first). */
  notices?: ClientNotice[];
  reports: Report[];
  quotations: Quotation[];
  invoices: Invoice[];
  feedback: FeedbackItem[];
  activity: Activity[];
  /** Formal events emailed to both parties, with the outcome of each send. */
  records?: SentRecord[];
  /** Payment certificates under the construction contract, mirrored from the
   *  Contract Studio (newest first). */
  certificates?: PaymentCertRecord[];
  estimate?: import("./rates").EstimateConfig;
  /** Once the scope and fee are settled the configuration is frozen: the client
   *  keeps full sight of it but can no longer change what was agreed. */
  feeLock?: { on: string; by: string; note?: string };
  /** Portal tabs the studio has explicitly turned OFF for the client. A key here
   *  hides that feature from the client view (on top of the automatic curation). */
  portalHidden?: string[];
  /** Curation overrides: pinned to the front row, or tucked into the menu. When a
   *  key is in neither, the project decides (see lib/portalNav). */
  portalPinned?: string[];
  portalTucked?: string[];
  reviews?: DrawingReview[];
  moodboard?: MoodBoard;
  engagement?: Engagement;
  /** Specialist sub-consultants the client has appointed (structural / MEP). */
  consultants?: Consultant[];
  /** When the concept directions were presented — until then they stay internal. */
  conceptsPresentedOn?: string;
  /** The agreed payment structure invoices are raised against. */
  billing?: PaymentSchedule;
  expenses?: Expense[];
  /** Construction-phase Requests for Information. */
  rfis?: RFI[];
  /** Variations / change orders — extra fee raised for scope changes. */
  variations?: Variation[];
  /** Site progress updates (construction monitoring). */
  siteLog?: SiteUpdate[];
  /** AI-extracted bill of quantities. */
  boq?: BoqItem[];
  /** Latest AI drawing-set QA audit. */
  qa?: QaReport;
  /** Defects / punch-list items captured at handover. */
  snags?: Snag[];
  /** Requests the scope sentinel judged to be beyond the agreed scope. */
  scopeFlags?: ScopeFlag[];
  /** Formal stage approvals the client has e-signed. */
  approvals?: Approval[];
  /** Tokened workspaces for consultants / contractors. */
  partners?: PartnerLink[];
  /** Site photos pinned to a point on a sheet. */
  photoPins?: PhotoPin[];
  /** Rates against the BOQ, and the cost plan they produce. */
  costPlan?: CostPlan;
  /** The agent's latest daily read of the project. */
  agentRun?: AgentRun;
  /** The preliminary construction budget conversation — the studio's rate, the
   *  client's money, and the area that falls out of the two. Drives the fee. */
  costBrief?: CostBrief;
  /** The design concept, as a presentation the client can be walked through. */
  deck?: ConceptDeck;
  /** Presentations that have been closed, newest first. Kept so the client can
   *  still read what they were shown at an earlier stage. */
  deckArchive?: ConceptDeck[];
  films?: ProjectFilm[];
  tenders?: TenderInvite[];
  followers?: TenderFollower[];
}

/* ---- tendering: the client's own invitations ----
 *
 * Clients almost always have contractors they want to see price the work. Rather
 * than emailing a folder of drawings around, they open a tender room: one link
 * that any invited firm can register on, which mints that firm its own access
 * code and gives it the documents. Who registered, and when, is then a record
 * rather than a memory of who was cc'd. */

export interface TenderRegistration {
  id: ID;
  code: string;                    // that firm's own access code
  firm: string;
  name: string;
  email: string;
  phone?: string;
  registeredOn: string;
  lastSeenOn?: string;
  /** The studio can rule a firm out without deleting the record of them asking. */
  status: "registered" | "declined" | "withdrawn";
}

/** Someone who just wants to watch the tender — a QS, a funder, a family member. */
export interface TenderFollower {
  id: ID;
  name: string;
  email: string;
  note?: string;                   // why they're watching
  addedBy: string;
  addedOn: string;
}

/** One priceable line of the tender's scope of works. */
export interface TenderScopeItem {
  id: ID;
  ref: string;                     // "1.03"
  description: string;
  qty?: number;
  unit?: string;
  note?: string;                   // basis / what governs it
}
/** A trade section of the scope — "Substructure", "Envelope", "MEP". */
export interface TenderScopeSection {
  id: ID;
  title: string;
  note?: string;
  items: TenderScopeItem[];
}
/** A document uploaded onto the tender itself — a BOQ, preambles, the form of
 *  tender — issued alongside the drawings. */
export interface TenderDoc {
  id: ID;
  name: string;
  url: string;
  addedOn: string;
  addedBy: string;
}

export interface TenderInvite {
  id: ID;
  ref: string;                     // TND-001
  title: string;
  /** The whole job, one package of it, or a hand-picked set of sheets. */
  scope: "whole" | "package" | "selected";
  packageId?: ID;                  // when scope is "package"
  /** Explicit sheet selection — the ONLY drawings issued when scope is
   *  "selected" (and a limit applied to "whole" when present). */
  docIds?: ID[];
  /** Documents uploaded onto the tender itself (BOQ, preambles, form of tender). */
  extraDocs?: TenderDoc[];
  /** The itemised scope of works the firms price against. */
  scopeSections?: TenderScopeSection[];
  note?: string;                   // what's being asked for, in the client's words
  deadline?: string;
  token: string;                   // the registration link
  createdBy: string;
  createdByRole: "client" | "studio";
  createdOn: string;
  closedOn?: string;
  registrations: TenderRegistration[];
}

/** A motion film or interactive presentation nested inside the project — the
 *  studio's own work, shown in place rather than sent as a link that dies. */
export interface ProjectFilm {
  id: ID;
  title: string;
  subtitle?: string;
  url: string;                     // same-origin path, or a URL the studio controls
  note?: string;                   // what the client should take from it
  aspect?: number;                 // width ÷ height; undefined = fill the frame
  addedOn: string;
  addedBy: string;
  sharedWithClient: boolean;
  autoplay?: boolean;              // it starts itself once on screen
}

/* ---- the concept presentation ---- */
export type SlideKind = "cover" | "film" | "statement" | "image" | "spaces" | "palette" | "options" | "closing";

export interface DeckSlide {
  id: ID;
  kind: SlideKind;
  title: string;
  body?: string;                   // the narrative on this slide
  images?: string[];
  /** How the slide is laid out when presented. */
  layout?: "full" | "split" | "grid";
  aiDrafted?: boolean;             // written by the assistant, not yet edited
  /** The passage from an uploaded document this slide was drawn from — studio-side
   *  provenance, so the architect can check the assistant against the source. */
  source?: { quote: string; where?: string };
  /** A film slide plays a nested ProjectFilm in place of images. */
  filmId?: ID;
  /** A full-quality video — a flythrough or animation — uploaded as-is to R2 and
   *  played in place. `images[0]` may hold a poster frame. */
  video?: string;
  videoName?: string;
}

/** A passage lifted verbatim out of an uploaded proposal or report, with what it
 *  was used for. The studio's audit trail on an AI-read document. */
export interface DeckSource {
  id: ID;
  quote: string;                   // the client's own words, unedited
  where?: string;                  // "page 3", "Section 2 — Brief"
  topic?: string;                  // what it speaks to
  usedFor?: string;                // the slide or option it informed
  file?: string;                   // which uploaded document it came from
}

/** One design option the client is asked to choose between. */
export interface DesignOption {
  id: ID;
  ref: string;                     // "A", "B", "C"
  name: string;
  thesis: string;                  // the idea in a sentence
  description: string;
  images: string[];
  strengths: string[];
  tradeoffs: string[];             // said plainly — every option costs something
  area?: number;                   // resulting area, where it differs
  costNote?: string;               // cost implication in words, not a false figure
  /* ---- when the option is one the studio has already DRAWN ---- */
  boardRef?: string;               // the sheet it lives on, e.g. "A101"
  page?: number;                   // which page of the uploaded set
  /** Figures read off the board itself — never computed, never estimated. */
  metrics?: { label: string; value: string }[];
  /** The design decisions the drawing embodies — the narration, in order. */
  decisions?: string[];
  response?: {
    choice: "preferred" | "considering" | "no";
    comment?: string;
    by: string; on: string;
  };
}

export interface DeckNote {
  id: ID;
  slideId?: ID;                    // which slide it's against, when it's specific
  optionId?: ID;
  body: string;
  by: string; on: string;
  author: "client" | "studio";
  reply?: { body: string; by: string; on: string };
}

export interface ConceptDeck {
  id: ID;
  title: string;
  subtitle?: string;
  slides: DeckSlide[];
  options: DesignOption[];
  /** "present" — the design is done and the deck presents it, reading the boards
   *  and narrating the decisions. "develop" — the assistant helps shape it. */
  mode?: "present" | "develop" | "design";
  status: "draft" | "issued";
  /** Which stage this presentation belongs to — "Concept Design", "Developed
   *  Design", "Technical Design". A project has one live deck and a history. */
  stage?: string;
  /** What sort of presentation it is. A concept deck argues between options; a
   *  render presentation shows the thing itself and asks how it feels; a design
   *  proposal walks through the resolved scheme. Different jobs, different
   *  formats. */
  kind?: "concept" | "renders" | "design";
  /** Closed presentations stay readable; they just stop being the live one. */
  closedOn?: string;
  createdOn: string;
  updatedOn: string;
  issuedOn?: string;
  /** Did the email actually go? Fire-and-forget hides a missing client address
   *  or a rejected send behind a green tick, so the outcome is recorded. */
  notify?: { to?: string; result: "delivered" | "simulated" | "failed"; error?: string; on: string };
  /** The client signing off the presentation as a whole. A concept deck asks
   *  them to choose between options; a design proposal has nothing to choose,
   *  so it asks them to accept it — or say what needs to change. */
  acceptance?: {
    status: "accepted" | "changes";
    comment?: string;
    by: string;
    on: string;
  };
  notes?: DeckNote[];
  /** Passages the assistant took from uploaded documents, and the file names it
   *  read. Studio-only — the client sees the narrative, not the working. */
  sources?: DeckSource[];
  readFiles?: string[];
}

/** The sizing conversation: what can be built for this budget, at this level of
 *  finish, and what fits inside the resulting area. */
export interface CostBrief {
  budget: number;                  // preliminary construction budget
  currency: string;
  quality: "standard" | "premium" | "bespoke";
  unit: "sqft" | "sqm";
  ratePerUnit: number;             // in the project's currency, per `unit`
  /** True when the studio set this project's rate by hand rather than taking
   *  it from the rate card. */
  rateOverride?: boolean;
  /** Locked once agreed — the client can still see it but no longer move it. */
  locked?: boolean;
  updatedOn: string;
  updatedBy: string;
  clientTouchedOn?: string;        // when the client last moved it themselves
}

/* ---- partner access, pinned photos, cost plan, agent ---- */
export interface PartnerLink {
  id: ID; ref: string;                 // PTR-001
  token: string;
  name: string;                        // firm / person
  email?: string;
  kind: "consultant" | "contractor";
  disciplines?: string[];              // which sheets they see; empty = all
  note?: string;
  createdOn: string; createdBy: string;
  lastSeenOn?: string;
  revoked?: boolean;
}

/* ---- what a partner sends back through their tokened workspace ---- */
export interface PartnerFile { name: string; url: string }

/** A design submission from a discipline partner — their drawings/calcs for
 *  the studio to review, without them ever seeing the rest of the project. */
export interface PartnerSubmission {
  id: ID; ref: string;                 // DS-001
  partnerId: ID; partnerName: string;
  discipline?: string;
  title: string;
  note?: string;
  files: PartnerFile[];
  on: string;
  status: "received" | "approved" | "revise";
  response?: string;
  respondedBy?: string; respondedOn?: string;
}

/** A cost proposal from a discipline partner — their price for their scope. */
export interface PartnerProposal {
  id: ID; ref: string;                 // CP-001
  partnerId: ID; partnerName: string;
  discipline?: string;
  title: string;
  amount: number;
  note?: string;
  files?: PartnerFile[];
  on: string;
  status: "received" | "accepted" | "declined";
  response?: string;
  respondedBy?: string; respondedOn?: string;
}

/* ---- formal stage submissions --------------------------------------------
 * The close of a design stage, packaged: every current sheet frozen as it
 * stood, the note that frames it, and a public token the client can open,
 * download from, or forward. The record of "this is what schematic design
 * concluded with". */
export interface StageSubmission {
  id: ID; ref: string;                 // SUB-001
  stage: StageKey;
  title: string;                       // "Concept Design — Stage Submission"
  note?: string;
  submittedOn: string; by: string;
  sheets: DrawingSetIssue["sheets"];
  /** The tokened public page (reuses the package viewer) — view/download/share. */
  packageToken?: string;
  clientViewedOn?: string;
}

export interface PhotoPin {
  id: ID;
  sheetCode: string; building?: string;
  x: number; y: number;                // 0..1 in sheet space
  url: string;                         // the photo
  note?: string;
  by: string; on: string;
  snagId?: ID;                         // when it became a snag
}

export interface CostLine {
  id: ID;
  boqItemId?: ID;                      // links back to the BOQ item
  description: string;
  qty: number; unit: string;
  rate: number;                        // cost per unit
  tendered?: number;                    // a real price returned by a contractor
  note?: string;
}
export interface CostPlan {
  currency: string;
  budget?: number;                     // the target construction cost
  lines: CostLine[];
  updatedOn: string;
  sharedWithClient?: boolean;
}

export interface AgentAction {
  kind: "email" | "chase" | "flag" | "task";
  title: string;
  detail: string;
  to?: string;                         // who it's aimed at
  draft?: string;                      // a ready-to-send message
  urgency: "high" | "medium" | "low";
  done?: boolean;
}
export interface AgentRun {
  ranOn: string;
  summary: string;
  actions: AgentAction[];
}

/* ---- formal stage sign-off ---- */
export interface Approval {
  id: ID; ref: string;                 // APP-001
  title: string;                       // "Design Development — drawing approval"
  stage?: string;
  note?: string;
  sheets: { code: string; title: string; revision?: string }[];
  status: "issued" | "signed" | "declined";
  issuedOn: string; issuedBy: string;
  signedBy?: string; signedOn?: string;
  signatureDataUri?: string;
  declineNote?: string;
}

/** A recurring charge that raises its own invoice on a cycle. */
export interface Retainer {
  id: ID; label: string;
  amount: number;
  cycle: "monthly" | "quarterly";
  startOn: string; endOn?: string;
  lastRunOn?: string;                  // date of the last invoice raised
  active: boolean;
}

/* ---- handover & scope ---- */
export type SnagStatus = "open" | "fixed" | "accepted";
export interface Snag {
  id: ID; ref: string;                 // SNG-001
  title: string; note?: string;
  location?: string; trade?: string;
  status: SnagStatus; severity?: "high" | "medium" | "low";
  photos: string[];
  raisedBy: string; raisedOn: string;
  fixedOn?: string; acceptedBy?: string; acceptedOn?: string;
}

/** A client request the AI judged to be outside the agreed brief/scope. */
export interface ScopeFlag {
  id: ID;
  source: "review" | "message" | "rfi";
  sourceRef?: string;                  // sheet code / RFI ref
  request: string;                     // what the client asked for
  verdict: "in-scope" | "out-of-scope" | "borderline";
  reason: string;
  suggestedTitle?: string;
  suggestedAmount?: number;
  flaggedOn: string;
  dismissed?: boolean;
  variationId?: ID;                    // set once raised as a variation
}

/** A saved project skeleton — register, stages, fee model — for repeat work. */
export interface ProjectTemplate {
  id: ID; name: string; note?: string;
  createdOn: string; createdBy: string;
  type?: string; types?: string[];
  deliverables: { code: string; title: string; discipline: string; phase: DeliverablePhase; building?: string }[];
  milestones?: { label: string; basis?: string; pct?: number }[];
  estimate?: import("./rates").EstimateConfig;
  briefSections?: { title: string; body: string }[];
}

/* ---- pre-appointment pipeline ---- */
export type LeadStatus = "enquiry" | "proposal" | "won" | "lost";
export interface Lead {
  id: ID; ref: string;                 // LEAD-001
  name: string;                        // the opportunity
  clientName: string; company?: string; email?: string; phone?: string;
  type?: string; location?: string;
  budget?: number; currency?: string;
  fee?: number;                        // proposed fee
  status: LeadStatus;
  source?: string;                     // referral, website, repeat…
  note?: string;
  createdOn: string;
  proposalSentOn?: string;
  decidedOn?: string; lostReason?: string;
  projectId?: ID;                      // set when converted
}

/* ---- construction-phase modules ---- */
export type RfiStatus = "open" | "answered" | "closed";
export type BallInCourt = "studio" | "client" | "consultant" | "contractor";
export interface RFI {
  id: ID; ref: string;                 // RFI-001
  subject: string; question: string;
  discipline?: string; sheetCode?: string;
  status: RfiStatus;
  ballInCourt: BallInCourt;
  raisedBy: string; raisedByRole: "studio" | "client" | "partner";
  raisedOn: string; dueOn?: string;
  attachmentUrl?: string;
  answer?: string; answeredBy?: string; answeredOn?: string;
}

export type VariationStatus = "draft" | "issued" | "approved" | "rejected";
export interface Variation {
  id: ID; ref: string;                 // VO-001
  title: string; description: string; reason?: string;
  amount: number;                      // fee impact, in the billing currency
  status: VariationStatus;
  createdOn: string; issuedOn?: string;
  decidedBy?: string; decidedOn?: string; decisionNote?: string;
  sourceReviewId?: string; sheetCodes?: string[];
  invoiceId?: string;                  // set once billed
}

export interface SiteUpdate {
  id: ID; on: string; by: string;
  title: string; note?: string;
  zone?: string; percent?: number;     // % complete for the zone/element
  photos: string[];                    // image URLs
}

export interface BoqItem {
  id: ID; code?: string;               // source sheet code
  discipline?: string;
  description: string;
  qty: number; unit: string;           // m², no., m, etc.
  note?: string;
  source: "ai" | "manual";
}

export interface QaFinding {
  severity: "high" | "medium" | "low";
  category: string;                    // "missing sheet", "coordination", "numbering", "consistency", "completeness"
  sheet?: string;                      // sheet code it relates to, if any
  finding: string;
  suggestion?: string;
}
export interface QaReport { runOn: string; by: string; summary: string; findings: QaFinding[] }

export interface Studio {
  name: string;
  tagline: string;
  principal: string;
  email: string;
  domain: string;
}
